/**
 * misradia (SPIKY) - collection stock sort, theme-only.
 *
 * Every purchasable product first, then every product that can't be bought,
 * across the WHOLE collection, keeping the order the shopper is viewing
 * (collection default, or any sort_by / filters they picked) inside each group.
 *
 * "In stock" = Liquid's product.available: at least one variant can be bought,
 * so variants set to continue selling at 0 stock count as in stock - the same
 * rule the "sold out" badge uses. No metafields, no filters, no apps.
 *
 * How:
 *  - templates/collection.stock-index.liquid (?view=stock-index) returns the
 *    collection in the shopper's current sort/filters as a light JSON index:
 *    [product id, available] for up to 250 products per request.
 *  - The index tells which products come next in each group; their cards are
 *    taken from the normal grid pages (section rendering), each page fetched
 *    once and cached.
 *  - Page 1 is server-rendered as usual; sold-out cards on it are rendered
 *    hidden (data-stock-deferred) so they never flash at the top.
 *  - The index is read gradually: one 250-product chunk at a time, ramping up
 *    to 4 in parallel only when a long run of one group has to be skipped.
 *  - When page 1 needs nothing, the first index chunk is prefetched once the
 *    browser is idle (not on Save-Data / 2G), so the first load-more click
 *    needs a single round trip.
 *
 * #items-grid data-stock-sort (set by main-collection-product-grid):
 *   full  - load-more pagination: global ordering
 *   local - other pagination types: reorder only when everything fits on one page
 *   off   - disabled in the theme editor
 *
 * Eurus' facets.js / pagination.js are untouched: grid re-renders are detected
 * with a MutationObserver and the load-more click is taken over in the capture
 * phase, before Alpine's @click handler runs.
 */
(function () {
  'use strict';
  if (window.MisradiaStockSort) return;

  var INDEX_VIEW = 'stock-index';
  var MAX_PARALLEL = 4;
  var state = null;

  function getGrid() {
    return document.getElementById('items-grid');
  }

  function gridItems(root) {
    return Array.prototype.slice.call(root.querySelectorAll(':scope > .grid-item'));
  }

  function findItem(grid, id) {
    if (!id) return null;
    return grid.querySelector(':scope > .grid-item[data-product-id="' + id + '"]');
  }

  function reveal(el) {
    el.style.display = el.getAttribute('data-stock-display') || 'block';
    el.removeAttribute('data-stock-deferred');
  }

  function revealInPlace(grid) {
    grid.querySelectorAll(':scope > .grid-item[data-stock-deferred]').forEach(reveal);
  }

  function currentParams(fromForm) {
    var params;
    var form = fromForm ? document.getElementById('FacetFiltersForm') : null;
    if (form) {
      params = new URLSearchParams(new FormData(form));
    } else {
      params = new URLSearchParams(window.location.search);
    }
    params.delete('page');
    params.delete('section_id');
    params.delete('view');
    return params;
  }

  function buildUrl(st, extra) {
    var p = new URLSearchParams(st.params.toString());
    Object.keys(extra).forEach(function (key) {
      p.set(key, extra[key]);
    });
    return window.location.pathname + '?' + p.toString();
  }

  function fetchText(url) {
    return fetch(url, { credentials: 'same-origin' }).then(function (response) {
      if (!response.ok) throw new Error('HTTP ' + response.status + ' ' + url);
      return response.text();
    });
  }

  function setLoading(st, value) {
    var box = document.getElementById('btn-pagination-' + st.sectionId);
    if (!box || !window.Alpine || !window.Alpine.$data) return;
    try {
      window.Alpine.$data(box).loading = value;
    } catch (e) {}
  }

  function updateUI(st) {
    if (!st || state !== st) return;
    if (st.emitted >= st.total) st.phase = 'done';

    var box = document.getElementById('btn-pagination-' + st.sectionId);
    if (!box) return;
    var label = box.querySelector('p');
    if (label && st.showingTpl) {
      label.textContent = st.showingTpl
        .replace('__X__', String(Math.min(st.emitted, st.total)))
        .replace('__Y__', String(st.total));
    }
    var button = box.querySelector('button');
    if (button) button.style.display = st.phase === 'done' ? 'none' : '';
  }

  /* ---------- index: [id, available] per position, 250 per request ---------- */

  function loadChunk(st, chunk) {
    if (st.chunks[chunk]) return st.chunks[chunk];
    st.chunks[chunk] = fetchText(buildUrl(st, { view: INDEX_VIEW, page: chunk + 1 }))
      .then(function (text) {
        var data = JSON.parse(text);
        var size = data.size || st.chunkSize;
        st.chunkSize = size;
        if (typeof data.total === 'number') st.total = data.total;
        (data.items || []).forEach(function (item, i) {
          st.index[chunk * size + i] = { id: String(item[0]), avail: item[1] === 1 };
        });
        st.loaded[chunk] = true;
      })
      .catch(function (error) {
        // Let a later call retry this chunk (e.g. a failed background prefetch).
        delete st.chunks[chunk];
        throw error;
      });
    return st.chunks[chunk];
  }

  // Next `count` positions of a group ('A' = can be bought, 'B' = can't),
  // continuing from where that group stopped last time.
  function nextPositions(st, group, count) {
    var wanted = group === 'A';
    var out = [];
    // Chunks requested at once: 1, then 2, then 4 while this call keeps
    // running out of index (a long run of the other group).
    var parallel = 1;

    function step() {
      while (out.length < count) {
        var pos = st.cursor[group];
        if (pos >= st.total) return Promise.resolve(out);

        var entry = st.index[pos];
        if (!entry) {
          var chunk = Math.floor(pos / st.chunkSize);
          if (st.loaded[chunk]) {
            // The collection is shorter than announced (changed meanwhile).
            st.total = pos;
            return Promise.resolve(out);
          }
          var batch = [];
          for (var c = chunk; c < chunk + parallel && c * st.chunkSize < st.total; c++) {
            batch.push(loadChunk(st, c));
          }
          parallel = Math.min(parallel * 2, MAX_PARALLEL);
          return Promise.all(batch).then(step);
        }

        st.cursor[group] = pos + 1;
        if (entry.avail === wanted) out.push({ pos: pos, id: entry.id });
      }
      return Promise.resolve(out);
    }

    return step();
  }

  /* ---------- cards: taken from the normal grid pages, cached ---------- */

  function loadGridPage(st, page) {
    if (st.pages[page]) return st.pages[page];
    st.pages[page] = fetchText(buildUrl(st, { section_id: st.sectionId, page: page })).then(function (html) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var grid = doc.getElementById('items-grid');
      if (!grid) return;
      gridItems(grid).forEach(function (el) {
        var id = el.getAttribute('data-product-id');
        if (id && !st.cards[id]) st.cards[id] = el;
      });
    });
    return st.pages[page];
  }

  function ensureCards(st, positions) {
    var needed = {};
    positions.forEach(function (p) {
      if (!st.cards[p.id]) needed[Math.floor(p.pos / st.perPage) + 1] = true;
    });
    return Promise.all(
      Object.keys(needed).map(function (page) {
        return loadGridPage(st, parseInt(page, 10));
      })
    );
  }

  function emit(st, positions) {
    positions.forEach(function (p) {
      st.emitted += 1;
      if (findItem(st.grid, p.id)) return;
      var src = st.cards[p.id];
      if (!src) return;
      var el = src.ownerDocument === document ? src : document.importNode(src, true);
      if (el.hasAttribute('data-stock-deferred')) reveal(el);
      st.grid.appendChild(el);
    });
  }

  function loadBatch(st, count) {
    var group = st.phase;
    if (group === 'done' || count <= 0) return Promise.resolve();

    return nextPositions(st, group, count).then(function (positions) {
      if (state !== st) return;
      return ensureCards(st, positions).then(function () {
        if (state !== st || !st.grid.isConnected) return;
        emit(st, positions);
        if (positions.length < count) {
          if (group === 'A') {
            // Nothing left to buy: continue straight into the sold-out group.
            st.phase = 'B';
            return loadBatch(st, count - positions.length);
          }
          st.phase = 'done';
        }
      });
    });
  }

  function run(st, count) {
    if (!st || st.busy || st.phase === 'done') return Promise.resolve();
    st.busy = true;
    setLoading(st, true);
    return loadBatch(st, count)
      .catch(function (error) {
        fail(st, error);
      })
      .then(function () {
        st.busy = false;
        setLoading(st, false);
        updateUI(st);
      });
  }

  function fail(st, error) {
    console.warn('[stock-sort] falling back to the default grid', error);
    if (state === st) state = null;
    if (!st.grid.isConnected) return;
    // Put any sold-out card of page 1 we were holding back at the end.
    st.held.forEach(function (el) {
      if (!el.isConnected) {
        reveal(el);
        st.grid.appendChild(el);
      }
    });
  }

  /* ---------- setup ---------- */

  function init(fromForm) {
    var grid = getGrid();
    state = null;
    if (!grid || grid.hasAttribute('data-stock-init')) return;
    grid.setAttribute('data-stock-init', '');

    var mode = grid.getAttribute('data-stock-sort') || 'off';
    var page = parseInt(grid.getAttribute('data-page'), 10) || 1;
    var pages = parseInt(grid.getAttribute('data-pages'), 10) || 1;
    var sectionId = grid.getAttribute('data-section-id');
    var items = gridItems(grid);
    var deferred = items.filter(function (el) {
      return el.hasAttribute('data-stock-deferred');
    });

    if (mode === 'off' || page !== 1) {
      revealInPlace(grid);
      return;
    }

    // Everything is on this page: just move the sold-out cards to the end.
    if (pages <= 1) {
      var anchor = items
        .filter(function (el) {
          return !el.hasAttribute('data-stock-deferred');
        })
        .pop();
      deferred.forEach(function (el) {
        if (anchor) anchor.after(el);
        anchor = el;
        reveal(el);
      });
      return;
    }

    var button = document.querySelector('#btn-pagination-' + sectionId + ' button');
    if (mode !== 'full' || !button) {
      revealInPlace(grid);
      return;
    }

    var st = {
      grid: grid,
      sectionId: sectionId,
      params: currentParams(fromForm),
      perPage: items.length || parseInt(grid.getAttribute('data-per-page'), 10) || 20,
      total: parseInt(grid.getAttribute('data-total'), 10) || 0,
      showingTpl: grid.getAttribute('data-showing-template') || '',
      phase: 'A',
      cursor: { A: 0, B: 0 },
      index: [],
      chunks: [],
      loaded: {},
      chunkSize: 250,
      pages: { 1: Promise.resolve() },
      cards: {},
      held: deferred,
      emitted: 0,
      busy: false
    };
    state = st;

    // Page 1 is already here: it gives the first positions of the index and
    // their cards. Its purchasable products are the start of group A.
    items.forEach(function (el, i) {
      var id = el.getAttribute('data-product-id');
      var avail = el.getAttribute('data-available') === 'true';
      st.index[i] = { id: id, avail: avail };
      st.cards[id] = el;
      if (avail) st.emitted += 1;
    });
    st.cursor.A = items.length;
    // Sold-out cards of page 1 leave the grid until group B reaches them.
    deferred.forEach(function (el) {
      el.remove();
    });

    if (deferred.length === 0) {
      updateUI(st);
      prefetchIndex(st);
      return;
    }
    run(st, deferred.length);
  }

  /* ---------- background prefetch of the first index chunk ---------- */

  function slowConnection() {
    var c = navigator.connection;
    if (!c) return false;
    return !!c.saveData || /(^|-)2g$/.test(c.effectiveType || '');
  }

  function whenIdle(fn) {
    var schedule = function () {
      if (window.requestIdleCallback) {
        window.requestIdleCallback(fn, { timeout: 3000 });
      } else {
        setTimeout(fn, 1500);
      }
    };
    if (document.readyState === 'complete') {
      schedule();
    } else {
      window.addEventListener('load', schedule, { once: true });
    }
  }

  function prefetchIndex(st) {
    if (slowConnection()) return;
    whenIdle(function () {
      if (state !== st || st.loaded[0] || st.chunks[0]) return;
      loadChunk(st, 0).catch(function () {});
    });
  }

  document.addEventListener(
    'click',
    function (event) {
      if (!state || !event.target || !event.target.closest) return;
      var button = event.target.closest('#btn-pagination-' + state.sectionId + ' button');
      if (!button) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      run(state, state.perPage);
    },
    true
  );

  // Filters / sorting: facets.js replaces #ProductGridContainer's content.
  function watch() {
    var container = document.getElementById('ProductGridContainer');
    if (!container) return;
    new MutationObserver(function () {
      if (getGrid()) {
        init(true);
      } else {
        state = null;
      }
    }).observe(container, { childList: true });
  }

  function start() {
    init(false);
    watch();
  }

  window.MisradiaStockSort = {
    init: init,
    loadMore: function () {
      return run(state, state ? state.perPage : 0);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
